<template>
  <div class="report-row">
    <div class="row">
      <div class="col-xs-12">
        <p><strong>{{ title }}</strong></p>
      </div>
    </div>

    <div v-if="showViewMode" class="form-group">
      <label class="radio-inline">
        <input type="radio" :value="'snapshot'" v-model="localViewMode"> Por ano
      </label>
      <label class="radio-inline">
        <input type="radio" :value="'evolution'" v-model="localViewMode"> Evolução
      </label>
    </div>

    <div class="form-group">
      <label class="radio-inline">
        <input type="radio" :value="'years'" v-model="localPeriodType"> Anos
      </label>
      <label class="radio-inline">
        <input type="radio" :value="'dates'" v-model="localPeriodType"> Datas
      </label>
    </div>

    <template v-if="localPeriodType === 'years' && isRange">
      <div class="row">
        <div class="col-xs-4 col-md-2">
          <p class="year-label"><strong>De</strong></p>
        </div>
        <div class="col-xs-8 col-md-10">
          <select v-model.number="localStartYear" class="form-control">
            <option v-for="year in availableYears" :key="'start-' + year" :value="year" :disabled="year > localEndYear">{{ year }}</option>
          </select>
        </div>
      </div>
      <div class="row">
        <div class="col-xs-4 col-md-2">
          <p class="year-label"><strong>Até</strong></p>
        </div>
        <div class="col-xs-8 col-md-10">
          <select v-model.number="localEndYear" class="form-control">
            <option v-for="year in availableYears" :key="'end-' + year" :value="year" :disabled="year < localStartYear">{{ year }}</option>
          </select>
        </div>
      </div>
    </template>

    <template v-if="localPeriodType === 'years' && !isRange">
      <select v-model.number="localYear" class="form-control">
        <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
      </select>
    </template>

    <template v-if="localPeriodType === 'dates'">
      <div class="form-group">
        <label>Data inicial</label>
        <DatePicker
          :bootstrapStyling="true"
          :language="datepickerLanguage"
          :value="startDateObject"
          @selected="onStartDateSelected"
          :typeable="true"
          format="dd-MM-yyyy"
        />
      </div>
      <div class="form-group">
        <label>Data final</label>
        <DatePicker
          :bootstrapStyling="true"
          :language="datepickerLanguage"
          :value="endDateObject"
          @selected="onEndDateSelected"
          :typeable="true"
          format="dd-MM-yyyy"
        />
      </div>
    </template>
  </div>
</template>

<script>
  import DatePicker from 'vuejs-datepicker'
  import { ptBR } from 'vuejs-datepicker/dist/locale'
  import { FIRST_AVAILABLE_YEAR, buildYearRange } from '@/constants/report_period'

  export default {
    name: 'ReportPeriodFilter',
    components: { DatePicker },
    props: {
      title: {
        type: String,
        default: 'Período'
      },
      isRange: {
        type: Boolean,
        default: true
      },
      showViewMode: {
        type: Boolean,
        default: false
      },
      viewMode: {
        type: String,
        default: 'snapshot'
      },
      periodType: {
        type: String,
        default: 'years'
      },
      startYear: Number,
      endYear: Number,
      year: Number
    },
    data () {
      return {
        datepickerLanguage: ptBR,
        localViewMode: this.viewMode,
        localPeriodType: this.periodType,
        localStartYear: this.startYear,
        localEndYear: this.endYear,
        localYear: this.year,
        localStartDate: `${this.startYear}-01-01`,
        localEndDate: `${this.endYear}-12-31`
      }
    },
    computed: {
      availableYears () {
        return buildYearRange(new Date().getFullYear(), FIRST_AVAILABLE_YEAR)
      },
      startDateObject () {
        return this.parseDate(this.localStartDate)
      },
      endDateObject () {
        return this.parseDate(this.localEndDate)
      }
    },
    watch: {
      localViewMode (value) {
        this.$emit('update:viewMode', value)
        this.emitChange()
      },
      localPeriodType () {
        this.emitChange()
      },
      localStartYear () {
        this.emitChange()
      },
      localEndYear () {
        this.emitChange()
      },
      localYear () {
        this.emitChange()
      },
      localStartDate () {
        this.emitChange()
      },
      localEndDate () {
        this.emitChange()
      }
    },
    methods: {
      parseDate (value) {
        if (!value) return null
        const parts = value.split('-')
        return new Date(parts[0], parts[1] - 1, parts[2])
      },
      formatDate (date) {
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${date.getFullYear()}-${month}-${day}`
      },
      onStartDateSelected (date) {
        if (!date) return
        this.localStartDate = this.formatDate(date)
      },
      onEndDateSelected (date) {
        if (!date) return
        this.localEndDate = this.formatDate(date)
      },
      emitChange () {
        this.$emit('change', this.getPeriod())
      },
      getPeriod () {
        return {
          viewMode: this.localViewMode,
          periodType: this.localPeriodType,
          startYear: this.localStartYear,
          endYear: this.localEndYear,
          year: this.localYear,
          startDate: this.localStartDate,
          endDate: this.localEndDate
        }
      }
    }
  }
</script>

<style scoped>
  .year-label {
    display: table-cell;
    vertical-align: middle;
    height: 34px;
  }
  label {
    color: white;
    font-weight: normal;
  }

  /* Garante legibilidade do calendário (evita herdar texto branco do tema escuro) */
  .report-row >>> .vdp-datepicker {
    position: relative;
  }
  .report-row >>> .vdp-datepicker__calendar {
    color: #333;
    background: #fff;
    border: 1px solid #ccc;
    z-index: 1000;
    right: 0;
    left: auto;
  }
  .report-row >>> .vdp-datepicker__calendar header,
  .report-row >>> .vdp-datepicker__calendar header span {
    color: #333;
  }
  .report-row >>> .vdp-datepicker__calendar .cell {
    color: #333;
  }
  .report-row >>> .vdp-datepicker__calendar .cell.day-header {
    color: #777;
  }
  .report-row >>> .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).day:hover,
  .report-row >>> .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).month:hover,
  .report-row >>> .vdp-datepicker__calendar .cell:not(.blank):not(.disabled).year:hover {
    border-color: #337ab7;
  }
  .report-row >>> .vdp-datepicker__calendar .cell.selected {
    background: #337ab7;
    color: #fff;
  }
  .report-row >>> .vdp-datepicker__calendar .cell.disabled {
    color: #ccc;
  }
</style>



// WEBPACK FOOTER //
// ReportPeriodFilter.vue?301709a6